// A belső Profilom „Egyéb adatok" részének tiszta segédei (nem-komponens): form-értékek, előtöltés, wire-body és a
// resolver a SZERZŐDÉS sémájából (`ProfilePersonalUpdateBodySchema` — nincs saját validálás). CSAK a belső felületen
// él: a partner-portál Profilomán ez a rész nem jelenik meg, így nem is validálódik.
import { type ProfilePersonalSchema, ProfilePersonalUpdateBodySchema } from '@atlas/contracts';
import { zodResolver } from '@hookform/resolvers/zod';
import { type TFunction } from 'i18next';
import { type Resolver } from 'react-hook-form';
import { type z } from 'zod';

export type ProfilePersonal = z.infer<typeof ProfilePersonalSchema>;
type PersonalBody = z.infer<typeof ProfilePersonalUpdateBodySchema>;

export interface PersonalFormValues {
  birthDate: string;
  nameDay: string;
  children: { fullName: string; birthDate: string }[];
  /** Kimaradás a kezdőlap „Ünnepeltek" csempéjéről (csak a belső Profilomon). */
  celebrationsOptOut: boolean;
}

export function personalToFormValues(p: ProfilePersonal): PersonalFormValues {
  return {
    birthDate: p.birthDate ?? '',
    nameDay: p.nameDay ?? '',
    children: p.children.map((c) => ({ ...c })),
    celebrationsOptOut: p.celebrationsOptOut,
  };
}

/** Form → wire-body: üres dátum → `null` (törlés); a gyerekek TELJES listája, a név vágva; a `version` az OCC. */
export function personalFormToBody(v: PersonalFormValues, version: number): PersonalBody {
  const day = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  return {
    birthDate: day(v.birthDate),
    nameDay: day(v.nameDay),
    children: v.children.map((c) => ({ fullName: c.fullName.trim(), birthDate: c.birthDate })),
    celebrationsOptOut: v.celebrationsOptOut,
    version,
  };
}

/** Lokalizált hibatérkép: a gyerek-sor mezői a közös `validation:people.children.*` üzeneteket kapják. */
function buildPersonalErrorMap(t: TFunction): z.ZodErrorMap {
  return (issue, ctx) => {
    if (issue.path[0] === 'children') {
      const sub = issue.path[2];
      if (sub === 'fullName' || sub === 'birthDate') {
        return { message: t(`validation:people.children.${sub}`) };
      }
    }
    return { message: ctx.defaultError };
  };
}

/** RHF-resolver a szerződés-sémából: a validálás a WIRE-alakon fut, a hiba-pathok 1:1 a form-mezők. */
export function buildPersonalResolver(t: TFunction): Resolver<PersonalFormValues> {
  const base = zodResolver(ProfilePersonalUpdateBodySchema, {
    errorMap: buildPersonalErrorMap(t),
  }) as unknown as Resolver<PersonalFormValues>;
  return async (values, context, options) => {
    // a verzió itt csak a séma kedvéért (0) — a valódit a mentés teszi a body-ba
    const result = await base(
      personalFormToBody(values, 0) as unknown as PersonalFormValues,
      context,
      options,
    );
    if (Object.keys(result.errors).length > 0) return { values: {}, errors: result.errors };
    return { values, errors: {} };
  };
}
